import React from 'react'
import { CreditApplicationStatus } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'

import AccountCreatedBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/AccountCreatedBanner'
import { GetCreditApplicationDetails_creditApplication_CreditApplication } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

import AwaitingSignatureBanner from './AwaitingSignatureBanner'
import ConfirmIdentityBanner from './ConfirmIdentityBanner'
import CorrectionNeededBanner from './CorrectionNeededBanner'
import FrozenCreditBanner from './FrozenCreditBanner'
import OfferAcceptedBanner from './OfferAcceptedBanner'
import OfferReadyBanner from './OfferReadyBanner'
import RefreshApplicationBanner from './RefreshApplicationBanner'
import ResumeApplicationBanner from './ResumeApplicationBanner'
import ReviewMigrationBanner from './ReviewMigrationBanner'
import styles from './styles.module.scss'
import UploadDocumentsBanner from './UploadDocumentsBanner'

type ActionItem =
  | 'provide_documents'
  | 'provide_corrections'
  | 'provide_identity'
  | 'review_offer'
  | 'unfreeze_credit'
  | 'refresh_application'
  | 'resume_application'
  | 'review_migration'
  | 'create_password'

export interface ActionsNeededSectionProps {
  creditApplication: GetCreditApplicationDetails_creditApplication_CreditApplication
  setActiveTabToDocuments: () => void
}

const ActionsNeededSection = ({ creditApplication, setActiveTabToDocuments }: ActionsNeededSectionProps) => {
  const [getClassName] = useNameFormatter('ActionsNeededSection')

  const { salesforceCreditId, applicationActionItems, personaInquiries, dsaVersion, authorizedSigner, status } =
    creditApplication || {}
  const authSignerEmail = authorizedSigner?.email || ''

  const hasActionItem = ({ requested, canBeCompleted = false }: { requested: ActionItem; canBeCompleted?: boolean }) =>
    applicationActionItems?.find(
      ({ action, completedAt }) => action === requested && (canBeCompleted ? true : !completedAt),
    )

  const createPasswordActionItem = hasActionItem({ requested: 'create_password', canBeCompleted: true })

  const reviewOfferActionItem = hasActionItem({ requested: 'review_offer', canBeCompleted: true })

  return (
    <div className={styles[getClassName('banners-container')]}>
      {/* Action items */}
      {/* From onboarding */}
      {hasActionItem({ requested: 'refresh_application' }) && (
        <RefreshApplicationBanner salesforceCreditId={salesforceCreditId} />
      )}
      {hasActionItem({ requested: 'resume_application' }) && (
        <ResumeApplicationBanner salesforceCreditId={salesforceCreditId} />
      )}
      {hasActionItem({ requested: 'review_migration' }) && (
        <ReviewMigrationBanner
          dsaVersion={dsaVersion}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {createPasswordActionItem && (
        <AccountCreatedBanner
          authorizedSigner={authorizedSigner}
          hasCreatedPassword={!!createPasswordActionItem.completedAt}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* from compliance */}
      {hasActionItem({ requested: 'provide_corrections' }) && (
        <CorrectionNeededBanner salesforceCreditId={salesforceCreditId} />
      )}
      {hasActionItem({ requested: 'provide_documents' }) && (
        <UploadDocumentsBanner
          salesforceCreditId={salesforceCreditId}
          setActiveTabToDocuments={setActiveTabToDocuments}
        />
      )}
      {hasActionItem({ requested: 'provide_identity' }) && (
        <ConfirmIdentityBanner
          personaInquiries={personaInquiries}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* from underwriting */}
      {reviewOfferActionItem && !reviewOfferActionItem?.completedAt && (
        <OfferReadyBanner
          authorizedSignerEmail={authSignerEmail}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {reviewOfferActionItem && !!reviewOfferActionItem?.completedAt && (
        <OfferAcceptedBanner salesforceCreditId={salesforceCreditId} />
      )}
      {hasActionItem({ requested: 'unfreeze_credit' }) && (
        <FrozenCreditBanner
          authSignerEmail={authSignerEmail}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {/* status derived banners */}
      {status === CreditApplicationStatus.AWAITING_SIGNATURE && (
        <AwaitingSignatureBanner salesforceCreditId={salesforceCreditId} />
      )}
    </div>
  )
}

export default ActionsNeededSection
